import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import { trackClickEvent, trackPageView } from './utils/analytics';

// Import critical components immediately
import { HomePage, StructuredData, Navbar } from './components';

// Lazy load non-critical pages for better performance
const AllServicesPage = lazy(() => import('./components/pages/AllServicesPage'));
const GalleryPage = lazy(() => import('./components/pages/GalleryPage'));
const FAQPage = lazy(() => import('./components/pages/FAQPage'));
const PrivacyPolicyPage = lazy(() => import('./components/pages/PrivacyPolicyPage'));
const ClientReviewPage = lazy(() => import('./components/pages/ClientReviewPage'));
const ServiceDetailPage = lazy(() => import('./components/pages/ServiceDetailPage'));
const KitchenRemodelingPage = lazy(() => import('./components/pages/services/KitchenRemodelingPage'));
const BathroomRemodelingPage = lazy(() => import('./components/pages/services/BathroomRemodelingPage'));
const GeneralContractorPage = lazy(() => import('./components/pages/GeneralContractorPage'));
const RoofingPage = lazy(() => import('./components/pages/RoofingPage'));
const SidingPage = lazy(() => import('./components/pages/SidingPage'));
const WindowsPage = lazy(() => import('./components/pages/WindowsPage'));
const GuttersPage = lazy(() => import('./components/pages/GuttersPage'));
const RepairsPage = lazy(() => import('./components/pages/RepairsPage'));
const RoofInsurancePage = lazy(() => import('./components/pages/RoofInsurancePage'));

// Loading component for lazy-loaded pages
const PageLoadingScreen = () => <div></div>;

// Scroll to top component for route changes
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }

    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
};

const RouteAnalyticsTracker = () => {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    trackPageView({
      path: `${pathname}${search}${hash}`,
      title: document.title
    });
  }, [pathname, search, hash]);

  return null;
};

const GlobalClickAnalyticsTracker = () => {
  useEffect(() => {
    const handleDocumentClick = (event) => {
      const targetElement = event.target;
      if (!(targetElement instanceof Element)) {
        return;
      }

      const clickableElement = targetElement.closest('button, a');
      if (!clickableElement) {
        return;
      }

      trackClickEvent(clickableElement);
    };

    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  return null;
};

const App = () => {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <RouteAnalyticsTracker />
        <GlobalClickAnalyticsTracker />
        <div className="App">
          <StructuredData />
          <Navbar />
          <Suspense fallback={<PageLoadingScreen />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/all-services" element={<AllServicesPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/review" element={<ClientReviewPage />} />
              <Route path="/services/kitchen-remodeling" element={<KitchenRemodelingPage />} />
              <Route path="/services/bathroom-remodeling" element={<BathroomRemodelingPage />} />
              <Route path="/services/:serviceSlug" element={<ServiceDetailPage />} />
              <Route path="/gc-services" element={<GeneralContractorPage />} />
              <Route path="/roofing" element={<RoofingPage />} />
              <Route path="/siding" element={<SidingPage />} />
              <Route path="/windows" element={<WindowsPage />} />
              <Route path="/gutters" element={<GuttersPage />} />
              <Route path="/repairs" element={<RepairsPage />} />
              <Route path="/roof-insurance" element={<RoofInsurancePage />} />
              <Route path="/diego" element={<Navigate to="/?source=diego_card#contact" replace />} />
              <Route path="/general-contractor" element={<Navigate to="/gc-services" replace />} />
              <Route path="/client-review" element={<Navigate to="/review" replace />} />
              {/* Catch-all route - redirects any non-existent paths to homepage */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </HelmetProvider>
  );
};

export default App;
